import { providerApi } from "@/services/api/providerApi";
import { useI18nStore } from "@/stores/i18nStore";
import { AffiliatePartnerType, PartnershipType } from "@/types/provider";
import React, { useState } from "react";
import toast from "react-hot-toast";
import { Link, useNavigate } from "react-router-dom";

export const AffiliatePartnerAdminRegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useI18nStore();

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: "",
    companyName: "",
    partnerType: AffiliatePartnerType.OTHER,
    partnershipType: PartnershipType.REFERRAL,
    address: "",
    phone: "",
    businessLicense: "",
    website: "",
    description: "",
  });

  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      toast.error(t.auth2PasswordsDoNotMatch);
      return;
    }

    setIsLoading(true);

    try {
      const { confirmPassword: _confirmPassword, ...registrationData } =
        formData;
      const response =
        await providerApi.registerAffiliatePartnerAdmin(registrationData);

      // Store user data manually (simplified approach)
      localStorage.setItem(
        "healthpass-auth",
        JSON.stringify({
          token: response.token,
          user: response.user,
        }),
      );

      toast.success(t.auth2AffiliateRegistered);
      navigate("/affiliate-admin");
    } catch (error: any) {
      console.error("Registration error:", error);
      toast.error(error.response?.data?.error?.message || t.registrationFailed);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <div className="flex items-center">
            <div className="w-8 h-8 bg-primary-600 rounded-lg flex items-center justify-center mr-3">
              <span className="text-white font-bold text-lg">H</span>
            </div>
            <span className="text-2xl font-bold text-gray-900">HealthPass</span>
          </div>
        </div>
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
          {t.auth2AffiliatePartnerAdmin}
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          {t.auth2AffiliateSubtitle}
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-lg">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <form className="space-y-6" onSubmit={handleSubmit}>
            {/* Personal Information */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label
                  htmlFor="firstName"
                  className="block text-sm font-medium text-gray-700"
                >
                  {t.firstName} *
                </label>
                <input
                  id="firstName"
                  name="firstName"
                  type="text"
                  required
                  value={formData.firstName}
                  onChange={handleInputChange}
                  className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm"
                  placeholder={t.auth2FirstNamePlaceholder}
                />
              </div>

              <div>
                <label
                  htmlFor="lastName"
                  className="block text-sm font-medium text-gray-700"
                >
                  {t.lastName} *
                </label>
                <input
                  id="lastName"
                  name="lastName"
                  type="text"
                  required
                  value={formData.lastName}
                  onChange={handleInputChange}
                  className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm"
                  placeholder={t.auth2LastNamePlaceholder}
                />
              </div>
            </div>

            {/* Email */}
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-700"
              >
                {t.email} *
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={formData.email}
                onChange={handleInputChange}
                className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm"
                placeholder={t.auth2PartnerEmailPlaceholder}
              />
            </div>

            {/* Password */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label
                  htmlFor="password"
                  className="block text-sm font-medium text-gray-700"
                >
                  {t.password} *
                </label>
                <div className="mt-1 relative">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="new-password"
                    required
                    value={formData.password}
                    onChange={handleInputChange}
                    className="appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm"
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={
                      showPassword ? t.auth2HidePassword : t.auth2ShowPassword
                    }
                  >
                    {showPassword ? (
                      <svg
                        className="h-5 w-5 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21"
                        />
                      </svg>
                    ) : (
                      <svg
                        className="h-5 w-5 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                        />
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              <div>
                <label
                  htmlFor="confirmPassword"
                  className="block text-sm font-medium text-gray-700"
                >
                  {t.confirmPassword} *
                </label>
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  required
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm"
                  placeholder="••••••••"
                />
              </div>
            </div>

            {/* Company Information */}
            <div className="border-t border-gray-200 pt-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {t.auth2CompanyInformation}
              </h3>

              <div>
                <label
                  htmlFor="companyName"
                  className="block text-sm font-medium text-gray-700"
                >
                  {t.auth2CompanyNameRequired}
                </label>
                <input
                  id="companyName"
                  name="companyName"
                  type="text"
                  required
                  value={formData.companyName}
                  onChange={handleInputChange}
                  className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm"
                  placeholder={t.auth2InsuranceCompanyPlaceholder}
                />
              </div>

              <div className="grid grid-cols-2 gap-4 mt-4">
                <div>
                  <label
                    htmlFor="partnerType"
                    className="block text-sm font-medium text-gray-700"
                  >
                    {t.auth2PartnerType}
                  </label>
                  <select
                    id="partnerType"
                    name="partnerType"
                    value={formData.partnerType}
                    onChange={handleInputChange}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  >
                    <option value={AffiliatePartnerType.TRAVEL_INSURANCE}>
                      {t.auth2PartnerTravelInsurance}
                    </option>
                    <option value={AffiliatePartnerType.HEALTH_INSURANCE}>
                      {t.auth2PartnerHealthInsurance}
                    </option>
                    <option value={AffiliatePartnerType.MEDICAL_EQUIPMENT}>
                      {t.auth2PartnerMedicalEquipment}
                    </option>
                    <option value={AffiliatePartnerType.PHARMACEUTICAL}>
                      {t.auth2PartnerPharmaceutical}
                    </option>
                    <option value={AffiliatePartnerType.WELLNESS_SERVICES}>
                      {t.auth2PartnerWellness}
                    </option>
                    <option value={AffiliatePartnerType.TELEMEDICINE}>
                      {t.auth2PartnerTelemedicine}
                    </option>
                    <option value={AffiliatePartnerType.DIAGNOSTIC_SERVICES}>
                      {t.auth2PartnerDiagnostics}
                    </option>
                    <option value={AffiliatePartnerType.EMERGENCY_ASSISTANCE}>
                      {t.auth2PartnerEmergencyAssistance}
                    </option>
                    <option value={AffiliatePartnerType.LOGISTICS_PROVIDER}>
                      {t.auth2PartnerLogistics}
                    </option>
                    <option value={AffiliatePartnerType.FINTECH}>
                      {t.auth2PartnerFintech}
                    </option>
                    <option value={AffiliatePartnerType.OTHER}>
                      {t.other}
                    </option>
                  </select>
                </div>

                <div>
                  <label
                    htmlFor="partnershipType"
                    className="block text-sm font-medium text-gray-700"
                  >
                    {t.auth2PartnershipType}
                  </label>
                  <select
                    id="partnershipType"
                    name="partnershipType"
                    value={formData.partnershipType}
                    onChange={handleInputChange}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  >
                    <option value={PartnershipType.REFERRAL}>
                      {t.auth2PartnershipReferral}
                    </option>
                    <option value={PartnershipType.COMMISSION_BASED}>
                      {t.auth2PartnershipCommission}
                    </option>
                    <option value={PartnershipType.REVENUE_SHARE}>
                      {t.auth2PartnershipRevenueShare}
                    </option>
                    <option value={PartnershipType.WHITE_LABEL}>
                      {t.auth2PartnershipWhiteLabel}
                    </option>
                    <option value={PartnershipType.RESELLER}>
                      {t.auth2PartnershipReseller}
                    </option>
                    <option value={PartnershipType.TECHNOLOGY_PARTNER}>
                      {t.auth2PartnershipTechnology}
                    </option>
                    <option value={PartnershipType.CONTENT_PARTNER}>
                      {t.auth2PartnershipContent}
                    </option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mt-4">
                <div>
                  <label
                    htmlFor="phone"
                    className="block text-sm font-medium text-gray-700"
                  >
                    {t.phoneNumber}
                  </label>
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm"
                    placeholder={t.auth2PhonePlaceholder}
                  />
                </div>

                <div>
                  <label
                    htmlFor="businessLicense"
                    className="block text-sm font-medium text-gray-700"
                  >
                    {t.auth2BusinessLicense}
                  </label>
                  <input
                    id="businessLicense"
                    name="businessLicense"
                    type="text"
                    value={formData.businessLicense}
                    onChange={handleInputChange}
                    className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm"
                    placeholder="LIC-12345-2024"
                  />
                </div>
              </div>

              <div>
                <label
                  htmlFor="website"
                  className="block text-sm font-medium text-gray-700 mt-4"
                >
                  {t.auth2Website}
                </label>
                <input
                  id="website"
                  name="website"
                  type="url"
                  value={formData.website}
                  onChange={handleInputChange}
                  className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm"
                  placeholder={t.auth2WebsitePlaceholder}
                />
              </div>

              <div>
                <label
                  htmlFor="address"
                  className="block text-sm font-medium text-gray-700 mt-4"
                >
                  {t.address}
                </label>
                <input
                  id="address"
                  name="address"
                  type="text"
                  value={formData.address}
                  onChange={handleInputChange}
                  className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm"
                  placeholder={t.auth2AddressPlaceholder}
                />
              </div>

              <div>
                <label
                  htmlFor="description"
                  className="block text-sm font-medium text-gray-700 mt-4"
                >
                  {t.description}
                </label>
                <textarea
                  id="description"
                  name="description"
                  rows={3}
                  value={formData.description}
                  onChange={handleInputChange}
                  className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm"
                  placeholder={t.auth2CompanyDescriptionPlaceholder}
                />
              </div>
            </div>

            {/* Submit Button */}
            <div className="pt-6">
              <button
                type="submit"
                disabled={isLoading}
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading && (
                  <svg
                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                )}
                {t.auth2RegisterAffiliateAdmin}
              </button>
            </div>

            {/* Role Info */}
            <div className="mt-6 p-4 bg-primary-50 border border-primary-200 rounded-md">
              <h4 className="text-sm font-medium text-primary-800 mb-2">
                {t.auth2AffiliateRoleHeading}
              </h4>
              <ul className="text-sm text-primary-700 space-y-1">
                <li>• {t.auth2AffiliateRoleServices}</li>
                <li>• {t.auth2AffiliateRoleAcquisition}</li>
                <li>• {t.auth2AffiliateRoleAnalytics}</li>
                <li>• {t.auth2AffiliateRoleBilling}</li>
                <li>• {t.auth2AffiliateRoleIntegration}</li>
              </ul>
            </div>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              {t.alreadyHaveAccount}{" "}
              <Link
                to="/login"
                className="font-medium text-primary-600 hover:text-primary-500"
              >
                {t.signIn}
              </Link>
            </p>
            <p className="mt-2 text-sm text-gray-600">
              {t.auth2OtherProviderType}{" "}
              <Link
                to="/register/provider"
                className="font-medium text-primary-600 hover:text-primary-500"
              >
                {t.auth2ProviderSelection}
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
